import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import SelectController from "@/common/components/SelectController";
import { Mapper } from "@/common/services/mapper";
import { FormTransformers } from "@/common/utils/formTransformers";
import { zodResolver } from "@hookform/resolvers/zod";
import { QueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { TrackerIntegrationValidationSchema, createSchema } from "../../../schemas/trackerIntegrations/create.schema";
import GoBackButton from "@/common/components/GoBackButton";
import useCreateTrackerIntegration from "../../../hooks/trackerIntegrations/useCreateTrackerIntegration";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useTrackerIntegrationOptions } from "@/common/hooks/queries/useTrackerIntegrationOptions";
import { TrackerIntegrationOptionsTransformer } from "../../../services/trackerIntegrations/trackerIntegrationOptionsTransformer";
import Checkbox from "@/common/components/Checkbox";
import { TrackerIntegrationOptionData } from "../../../types/trackerIntegrationOption.type";
import TextField from "@/common/components/TextField";
import { TrackerIntegrationsTransformer } from "@/features/privateCompanies/services/trackerIntegrations/trackerIntegrationsTransformer";
import useDebounce from "@/common/hooks/useDebounce";

const Create = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const queryClient = new QueryClient()
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchTrackerIntegration, setSearchTrackerIntegration] = useDebounce('');

  const [parameters, setParameters] = useState<TrackerIntegrationOptionData['required_parameters']>({})

  const {
    mutate,
    isPending: createCompanyTrackerIntegrationIsPending
  } = useCreateTrackerIntegration({
    companyId: id || '',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trackerIntegrations'] })
      navigate(`/companies/${id}/tracker-integrations`)
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({
        setError,
        setMessages,
        transformer: TrackerIntegrationsTransformer.errorsTransformer(),
      })(error);
    }
  })

  const {
    handleSubmit,
    control,
    setError,
    register,
    formState: { errors }
  } = useForm<TrackerIntegrationValidationSchema>({
    resolver: zodResolver(createSchema)
  });

  const { data: trackerIntegrationOptions } = useTrackerIntegrationOptions({
    filters: { search: searchTrackerIntegration },
    mapper: Mapper.mapWithTransformer({ transformer: TrackerIntegrationOptionsTransformer.optionsTransformer() }),
  });

  const onSubmit: SubmitHandler<TrackerIntegrationValidationSchema> = (data) => mutate(data);

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="false">
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-4 items-center">
          <GoBackButton fallback={`/companies/${id}/tracker-integrations`} />
          <h2 className="text-2xl font-bold">Nova integração de rastreamento</h2>
        </div>
        <div className="flex gap-x-4 items-center">
          <Link to={`/companies/${id}/tracker-integrations`}>
            <Button variant="outline-error">Cancelar</Button>
          </Link>
          <Button
            name="submit"
            type="submit"
            className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
            isLoading={createCompanyTrackerIntegrationIsPending}
          >
            Confirmar
          </Button>
        </div>
      </div>

      <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
        <p className="text-lg font-bold">
          Nova integração de rastreamento
        </p>

        <SelectController
          control={control}
          name="option_id"
          label="Tipo de integração"
          markAsRequired
          data-testid="option_id_selector"
          triggerOnChange={(e: any) => setParameters(e?.parameters)}
          onInputChange={setSearchTrackerIntegration}
          options={trackerIntegrationOptions || []}
          errorMessage={errors?.option_id?.message || undefined}
        />

        {Object.keys(parameters).length > 0 && (
          <div className="grid grid-cols-2 gap-y-4 gap-x-2 bg-">
            {parameters &&
              Object.keys(parameters)
                .sort((a, b) => a.localeCompare(b))
                .map((param) => (
                  <TextField
                    label={param}
                    errorMessage={errors?.parameters?.[param]?.message}
                    markAsRequired
                    {...register(`parameters.${param}`)}
                  />
                ))}
          </div>
        )}

        <Checkbox
          label="Habilitada"
          {...register('active')}
        />
      </Box>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </form>
  );
};

export default Create;
